import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartIcon, BrainIcon, SparklesIcon, SunIcon, Users, ArrowRightIcon, MenuIcon, XIcon, MoonIcon } from 'lucide-react';
import { ContactSection } from '../components/ContactSection';
import { CourseCalendar } from '../components/CourseCalendar';
import { HeroRotator } from '../components/HeroRotator';
import { currentCourse } from '../data/courses';
// ═══════════════════════════════════════════════════════════
// 🔗 REGISTRACIJOS NUORODA — čia įrašykite savo anketos URL
// ═══════════════════════════════════════════════════════════
const REGISTRATION_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdN1pDWRqjo_eykjFE7VECqifWUKuHBRr2Fq-LVvCOnP6_lwg/viewform?usp=publish-editor';
export function LandingPage({
  mode,
  toggleMode,
  onViewCourses
}: {
  mode: 'light' | 'dark';
  toggleMode: () => void;
  onViewCourses: () => void;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [heroDark, setHeroDark] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Ant tamsios hero skaidrės navigacija turi būti balta
  const navOnDark = scrolled || heroDark;
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [testimonialDirection, setTestimonialDirection] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 30
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    },
    transition: {
      duration: 0.65,
      ease: 'easeOut'
    }
  };
  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.1
      }
    },
    viewport: {
      once: true,
      margin: '-80px'
    }
  };
  // Reusable section label
  const SectionLabel = ({
    text,
    light = false
  }: {
    text: string;
    light?: boolean;
  }) => <div className="flex flex-col items-center mb-5" data-id="element-128" data-mp="3e6r0pgmreh0k">
      <p className={`text-xs uppercase tracking-widest font-semibold mb-3 ${light ? 'text-gold-light/70' : 'text-gold-dark'}`} data-id="element-129" data-mp="3e8bvn7czk1yy">
        {text}
      </p>
      <div className="w-10 h-0.5 bg-gold" data-id="element-130" data-mp="3o4552hverfci" />
    </div>;
  return <main className="w-full bg-sand-lightest transition-colors duration-300" data-id="element-131" data-mp="3j5v8q3xy68pb">
      {/* ── STICKY NAV ── */}
      <motion.nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-500" style={{
      background: scrolled ? 'rgba(var(--mountain-darker), 0.96)' : 'transparent',
      backdropFilter: scrolled ? 'blur(12px)' : 'none',
      boxShadow: scrolled ? '0 1px 0 rgba(255,255,255,0.06)' : 'none'
    }} initial={{
      y: -80,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.6,
      ease: 'easeOut',
      delay: 2.0
    }} data-id="element-132" data-mp="rmceia1hae2">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-133" data-mp="ffcnqqt4se38">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 select-none" data-id="element-134" data-mp="kccqosuop0q4">
            <span className="font-accent tracking-wide text-[#F72585] text-[32px] font-[500]" data-id="element-135" data-mp="6lpr6yh3n6sz">
              tavo būsena
            </span>
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8" data-id="element-136" data-mp="3stpon1meqtaj">
            {[{
            label: 'Apie',
            href: '#apie'
          }, {
            label: 'Metodika',
            href: '#metodika'
          }, {
            label: 'Programa',
            href: '#programa'
          }, {
            label: 'Komanda',
            href: '#komanda'
          }, {
            label: 'Tvarkaraštis',
            href: '#tvarkarastis'
          }].map(link => <a key={link.label} href={link.href} className={`text-sm font-medium uppercase tracking-wider transition-colors duration-200 ${navOnDark ? 'text-white/70 hover:text-white' : 'text-[#1B1B4B]/70 hover:text-[#1B1B4B]'}`} data-id="element-137" data-mp="370lc7gan0t52">
                {link.label}
              </a>)}
          </div>

          {/* CTA & Dark Mode */}
          <div className="hidden md:flex items-center gap-4" data-id="element-138" data-mp="2o3k4hnpp8exf">
            <button onClick={toggleMode} className={`p-2 rounded-full transition-colors ${navOnDark ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-[#1B1B4B]/80 hover:text-[#1B1B4B] hover:bg-[#1B1B4B]/10'}`} aria-label="Toggle dark mode" data-id="element-139" data-mp="2j3fqq5g6dyaw">
              {mode === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-140" data-mp="psaai6fd9iui" /> : <SunIcon className="w-5 h-5" data-id="element-141" data-mp="tu57iyq9la2h" />}
            </button>
            <motion.a href="https://www.instagram.com/vestirna/" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-2 text-white px-6 py-2.5 rounded-full text-sm font-semibold transition-colors shadow-lg" style={{
            background: 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)'
          }} data-id="element-142" data-mp="yldgxurlgvfn">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" data-id="element-143" data-mp="krvykdan9or7">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-144" data-mp="1r9j2ukudwqh2" />
              </svg>
              Parašyk mums
            </motion.a>
          </div>

          {/* Mobile burger & Dark Mode */}
          <div className="flex items-center gap-2 md:hidden" data-id="element-145" data-mp="51oaec5nwla9">
            <button onClick={toggleMode} className={`p-2 rounded-full transition-colors ${navOnDark ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-[#1B1B4B]/80 hover:text-[#1B1B4B] hover:bg-[#1B1B4B]/10'}`} aria-label="Toggle dark mode" data-id="element-146" data-mp="4ekywi9w9mf">
              {mode === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-147" data-mp="26sx2m6nnsgoq" /> : <SunIcon className="w-5 h-5" data-id="element-148" data-mp="26tgcr172i0s7" />}
            </button>
            <button className={`p-1 transition-colors ${navOnDark ? 'text-white/80 hover:text-white' : 'text-[#1B1B4B]/80 hover:text-[#1B1B4B]'}`} onClick={() => setMobileOpen(!mobileOpen)} aria-label="Meniu" data-id="element-149" data-mp="2ujalp7o9n576">
              {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-150" data-mp="2pi3cd46h5xye" /> : <MenuIcon className="w-6 h-6" data-id="element-151" data-mp="1a2ebqudy4rrl" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence data-id="element-152" data-mp="1e62hd7j0pda7">
          {mobileOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} className="md:hidden overflow-hidden" style={{
          background: '#1e0664'
        }} data-id="element-153" data-mp="25sltlojf8b7g">
              <div className="px-6 py-4 flex flex-col gap-4 border-t border-white/10" data-id="element-154" data-mp="2khi926nt4qi3">
                {[{
              label: 'Apie',
              href: '#apie'
            }, {
              label: 'Metodika',
              href: '#metodika'
            }, {
              label: 'Programa',
              href: '#programa'
            }, {
              label: 'Komanda',
              href: '#komanda'
            }, {
              label: 'Tvarkaraštis',
              href: '#tvarkarastis'
            }].map(link => <a key={link.label} href={link.href} onClick={() => setMobileOpen(false)} className="text-white/80 hover:text-white text-base font-medium uppercase tracking-wider py-1 transition-colors" data-id="element-155" data-mp="3oqs4hr5asrkr">
                    {link.label}
                  </a>)}
                <a href="https://www.instagram.com/vestirna/" target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className="inline-flex items-center justify-center gap-2 text-white px-6 py-3 rounded-full text-sm font-semibold text-center mt-2" style={{
              background: 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)'
            }} data-id="element-156" data-mp="7cda3ugfwp99">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" data-id="element-157" data-mp="1q5nyox66ddvz">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-158" data-mp="1uy7dtykigh2w" />
                  </svg>
                  Parašyk mums
                </a>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.nav>

      {/* ── HERO (rotuojamas) ── */}
      <HeroRotator onToneChange={setHeroDark} data-id="element-159" data-mp="3boi02oqxqycn" />

      {/* ── INTRO ── */}
      <section id="apie" className="py-20 px-4 bg-sand-light" data-id="element-160" data-mp="3bp9nhcadppru">
        <motion.div className="max-w-3xl mx-auto text-center" {...fadeInUp} data-id="element-161" data-mp="36oc5p0ta6xqv">
          <p className="text-lg md:text-xl text-mountain-dark leading-relaxed font-[300]" data-id="element-162" data-mp="31qts2x4bjucj">
            Ne visada nuo tavęs priklauso, kas nutiks tavo gyvenime, tačiau gali sąmoningai spręsti, kaip reaguosi į tai, kas vyksta. Šis kursas padės geriau pažinti savo reakcijas, valdyti dėmesį ir sąmoningai pasirinkti būseną, kurioje nori būti. Gyventi ne iš įpročio, o iš pasirinkimo. Čia prasideda tikroji tavo laisvė.
          </p>
        </motion.div>
      </section>

      {/* ── CHALLENGES ── */}
      <section className="py-24 px-4 bg-sand-lightest" data-id="element-163" data-mp="1is5na4j95buh">
        <div className="max-w-6xl mx-auto" data-id="element-164" data-mp="1ircl7gv8naio">
          <motion.div className="mb-12" {...fadeInUp} data-id="element-165" data-mp="1nq7fpg2kinim">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-166" data-mp="1nstp7l90rd3h">
              Visiems Pažįstami
            </p>
            <div className="h-[2px] w-[160px] mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-167" data-mp="1dsjafw7c5xm7" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-168" data-mp="1439g09p0s2ux">
              Kasdieniai Iššūkiai
            </h2>
          </motion.div>

          <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-4" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-169" data-mp="v2l2rh7ga6j3">
            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-170" data-mp="lez04cj0yt9l">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1537210249814-b9a10a161ae4?w=600&q=80')"
            }} data-id="element-171" data-mp="uecb59msrpwf" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-172" data-mp="z644frxc89q9" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-173" data-mp="191xdv2frfn3t">
                01
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-174" data-mp="3cewoqfzzxr2q">
                <p className="text-lg font-bold text-white mb-1" data-id="element-175" data-mp="3hb3ry110cogz">
                  Nuolatinis stresas
                </p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-176" data-mp="2jq7tuypjahzf">
                  Kaip efektyviai spręsti problemas neprarandant vidinės
                  ramybės?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-177" data-mp="2xo5wx7yemrzm">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://cdn.magicpatterns.com/uploads/fkDNhwZwL2Fman3fp7TuCW/moritz-kindler-n6XW1P6a-_U-unsplash.jpg')"
            }} data-id="element-178" data-mp="cwnke552fwb2" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-179" data-mp="1om4bezq0mshh" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-180" data-mp="19q52jmv30oba">
                02
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-181" data-mp="302ktl0syfn81">
                <p className="text-lg font-bold text-white mb-1" data-id="element-182" data-mp="2v386qg232g5g">
                  Dėmesio blaškymasis
                </p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-183" data-mp="3e7bcaxyxjemh">
                  Kaip valdyti dėmesį nuolat besikeičiančioje aplinkoje?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-184" data-mp="357y2g46w8o18">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1669369172813-938c5aaa8685?w=600&q=80')"
            }} data-id="element-185" data-mp="1rhk368vw622" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-186" data-mp="3sw7sjib53xy6" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-187" data-mp="2hf7tm3uaekv6">
                03
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-188" data-mp="2dzwhefrjpmwj">
                <p className="text-lg font-bold text-white mb-1" data-id="element-189" data-mp="2dxk2kbh33snp">Perdegimas</p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-190" data-mp="1qalofkgfswsy">
                  Kaip siekti ilgalaikių tikslų neperdegant?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-191" data-mp="200ndaegm6j8x">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1516490981167-dc990a242afe?w=600&q=80')"
            }} data-id="element-192" data-mp="17hzy1foatyfr" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-193" data-mp="m1atpurkcidy" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-194" data-mp="10xs8kvyyof59">
                04
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-195" data-mp="1ep6o2stnty7k">
                <p className="text-lg font-bold text-white mb-1" data-id="element-196" data-mp="1jo052v85o7ch">Chaosas</p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-197" data-mp="1jlx3rf1jyrsg">
                  Ką daryti su emocijomis sudėtingose situacijose?
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── TARP NUOLATINĖS KAITOS ── */}
      <section className="py-24 px-4 bg-mountain-darker text-white" data-id="element-198" data-mp="344r6esscjeaa">
        <div className="max-w-6xl mx-auto" data-id="element-199" data-mp="2z80pk38pwent">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-200" data-mp="2z8smbhe1wifo">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-201" data-mp="2u9fpc3tgtuzd">
              Paieškos
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-202" data-mp="3ruv6iq6bsvwj" />
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight" data-id="element-203" data-mp="3d5pd5iu65zwk">
              Tarp nuolatinės kaitos ir pusiausvyros paieškų
            </h2>
          </motion.div>

          <motion.div className="flex flex-col gap-12 items-center" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-204" data-mp="9prz2ysw07sy">
            <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto" data-id="element-205" data-mp="1r9o2pm5rpmq6">
              <p className="text-[20px] text-white/80 font-[300]" data-id="element-206" data-mp="1mceaxzuwoyz6">
                Šiandienos pasaulis sukasi neregėtu tempu. Informacijos srautas,
                nuolatiniai reikalavimai ir pokyčiai kuria vidinę įtampą, kuri
                laikui bėgant virsta stresu, perdegimo jausmu ir prarastu ryšiu
                su savimi.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="w-full max-w-2xl mx-auto space-y-0" data-id="element-207" data-mp="25aqnlvl10qzi">
              {['Kaip efektyviai spręsti problemas neprarandant vidinės ramybės?', 'Kaip giliai atsipalaiduoti ir tikrai pailsėti?', 'Kaip pasiekti gyvenime harmoniją ir pilnatvę?', 'Kaip valdyti emocijas ir dėmesį sudėtingose situacijose?'].map((question, i) => <div key={i} className="flex items-start gap-5 py-6" style={{
              borderBottom: i < 3 ? '1px solid rgba(255,255,255,0.1)' : 'none'
            }} data-id="element-208" data-mp="3f7fc5y2wk9fo" data-mpi={i}>
                  <span className="font-bold text-gold-light flex-shrink-0" style={{
                fontSize: '2rem',
                lineHeight: 1.1
              }} data-id="element-209" data-mp="3t7q3t3c1ds1w">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-white/80 text-lg leading-relaxed pt-0.5" data-id="element-210" data-mp="au8ptqspsuyz">
                    {question}
                  </p>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── WHAT IS DFB ── */}
      <section id="metodika" className="relative overflow-hidden" style={{
      minHeight: 600
    }} data-id="element-211" data-mp="27ud0b8bieuhg">
        {/* Full-width background image */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1920&q=80')"
      }} data-id="element-212" data-mp="2lnm66z090xv8" />
        {/* Left-to-right cinematic gradient */}
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(to right, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.72) 40%, rgba(0,0,0,0.25) 70%, transparent 100%)'
      }} data-id="element-213" data-mp="2qmyrhfbh1wvp" />

        {/* Content — left half */}
        <motion.div className="relative z-10 flex items-center" style={{
        minHeight: 600
      }} {...fadeInUp} data-id="element-214" data-mp="1a74h5jyzdxim">
          <div className="max-w-7xl mx-auto w-full px-8 md:px-16 py-24" data-id="element-215" data-mp="3guld7vpq5zoy">
            <div className="max-w-xl" data-id="element-216" data-mp="3ltoinh9ma7xg">
              {/* Italic subtitle — now above */}
              <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-217" data-mp="382jeq2z7uthb">
                Sprendimas
              </p>
              {/* Gold line */}
              <div className="h-[2px] w-[160px] mb-4 rounded-full" style={{
              background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
            }} data-id="element-218" data-mp="3c2tto54zku8b" />
              {/* Section heading */}
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-tight" data-id="element-219" data-mp="2p59wuq9jho26">
                Sąmoningas Būsenų Valdymas
              </h2>
              {/* Paragraphs */}
              <p className="text-white/80 mb-5 font-[300] text-[20px]" data-id="element-220" data-mp="ndna50wokfbi">
                Diferencijuotos Funkcinės Būsenos – tai aktyvios
                savireguliacijos metodika, leidžianti sąmoningai valdyti vidines
                būsenas nepriklausomai nuo išorinės veiklos.
              </p>
              <p className="text-white/80 font-[300] text-[20px]" data-id="element-221" data-mp="3tuvuv3cupbp4">
                Metodika unikali tuo, kad moko ne slopinti emocijas, o jas
                diferencijuoti, sąmoningai pasirinkti ir pritaikyti.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── 16 STATES ── (dark bg) */}
      <section className="py-24 px-4 bg-mountain-darker text-white" data-id="element-222" data-mp="3fvmhscjxyxpd">
        <div className="max-w-6xl mx-auto" data-id="element-223" data-mp="3b1ia1r2uqln8">
          <motion.div className="text-center mb-10" {...fadeInUp} data-id="element-224" data-mp="363qhqvyaoxii">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-225" data-mp="3649rvqhpehlz">
              Aktyvios Savireguliacijos Sistema
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-226" data-mp="2w6c1awcdg8q7" />
            <h3 className="text-3xl md:text-4xl font-bold text-white" data-id="element-227" data-mp="17o96gi66rwex">
              16 Funkcinių Būsenų
            </h3>
          </motion.div>

          <motion.div className="mb-16 max-w-3xl mx-auto" {...fadeInUp} data-id="element-228" data-mp="1gml597h7i0ig">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-2xl text-center" data-id="element-229" data-mp="1llxv813djnp9">
              <p className="text-white/80 leading-relaxed" data-id="element-230" data-mp="nypcgdurcsqy">
                DFB metodika dalinasi į žvalgą: kiekvienai veiklai reikalinga
                skirtinga psichikos būsena. Trumpų distancijų bėgimas reikalauja
                kitokio pasirengimo nei maratonas. Lengvas pokalbis – tai viena
                būsena, o intensyvus protinis darbas – visai kita.
              </p>
            </div>
          </motion.div>

          {/* Vertical Timeline Flow */}
          <motion.div className="relative max-w-4xl mx-auto py-8" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-231" data-mp="55lewhef5vr2">
            {/* Central Vertical Line */}
            <div className="absolute left-8 md:left-1/2 top-12 bottom-12 w-0.5 bg-gradient-to-b from-gold/60 via-gold/40 to-gold/20 -translate-x-1/2" data-id="element-232" data-mp="bwflhuoeto5v" />

            <div className="flex flex-col gap-16 md:gap-20 relative z-10" data-id="element-233" data-mp="6xl3guy9o3mt">
              {[{
              title: '4 Mentalinio Aktyvumo Būsenos',
              desc: 'Nuo chaoso iki tikslaus mąstymo',
              img: 'https://cdn.magicpatterns.com/uploads/86D17L5NgjqReE3GCNF7d3/Screenshot_2026-03-06_at_13.44.55.png',
              imgStyle: {
                backgroundSize: '250%',
                backgroundPosition: '55% 35%'
              } as React.CSSProperties
            }, {
              title: '4 Emocinio Aktyvumo Būsenos',
              desc: 'Nuo gilumo ir jautrumo iki lengvumo ir dinamiškumo bendraujant',
              img: 'https://cdn.magicpatterns.com/uploads/oYRvzX8HGPdzN3RmPEhdeg/zac-durant-_6HzPU9Hyfg-unsplash.jpg',
              imgStyle: {} as React.CSSProperties
            }, {
              title: '4 Fizinio Aktyvumo Būsenos',
              desc: 'Nuo plačių, erdvinių judesių iki tikslingo, koordinuotos veiklos',
              img: 'https://cdn.magicpatterns.com/uploads/6RbKwQ7msGyRimZEELWpys/atharva-tulsi-2jA_YoQuvaA-unsplash.jpg',
              imgStyle: {} as React.CSSProperties
            }, {
              title: '4 Harmoningo Aktyvumo Būsenos',
              desc: 'Kūnas, emocijos ir protas veikia darniai kaip viena visuma',
              img: 'https://cdn.magicpatterns.com/uploads/deWv2jrD7JtPFbuwEysfBb/IMG_7871.jpg',
              imgStyle: {
                backgroundSize: '180%',
                backgroundPosition: '50% 40%'
              } as React.CSSProperties
            }].map((item, idx) => {
              const isEven = idx % 2 === 0;
              return <motion.div key={idx} variants={fadeInUp} className={`relative flex flex-col md:flex-row items-start md:items-center w-full ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-234" data-mp="1f4maku3swnz6" data-mpi={idx}>
                    {/* Mobile Dot */}
                    <div className="md:hidden absolute left-8 top-12 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gold ring-4 ring-mountain-darker z-20" data-id="element-235" data-mp="22tp0sqci3p5a" />

                    {/* Desktop Center Dot */}
                    <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gold ring-4 ring-mountain-darker z-20" data-id="element-236" data-mp="1tuadfzrmrup0" />

                    {/* Image Side */}
                    <div className={`w-full md:w-1/2 flex pl-20 md:pl-0 ${isEven ? 'md:justify-end md:pr-16' : 'md:justify-start md:pl-16'} mb-5 md:mb-0`} data-id="element-237" data-mp="idkdgydsvz3u">
                      <div className="relative z-10 w-24 h-24 rounded-full overflow-hidden border-4 border-gold ring-2 ring-gold/25 flex-shrink-0 shadow-xl" data-id="element-238" data-mp="2y43lqmjwztyu">
                        <div className="w-full h-full bg-cover bg-center" style={{
                      backgroundImage: `url('${item.img}')`,
                      ...item.imgStyle
                    }} data-id="element-239" data-mp="37su0z03ytefs" />
                      </div>
                    </div>

                    {/* Text Side */}
                    <div className={`w-full md:w-1/2 flex flex-col pl-20 md:pl-0 ${isEven ? 'md:items-start md:pl-16' : 'md:items-end md:pr-16 md:text-right'}`} data-id="element-240" data-mp="3qrpsg3ofvepx">
                      <h4 className="font-bold text-white mb-2 text-[20px]" data-id="element-241" data-mp="1wdf21qs74ci1">
                        {item.title}
                      </h4>
                      <p className="text-sm text-white/60 leading-relaxed max-w-sm" data-id="element-242" data-mp="1hy5p9236mmpq">
                        {item.desc}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── BENEFITS ── (light bg) */}
      <section className="py-24 px-4 bg-sand-lightest" data-id="element-243" data-mp="20q888k3aqf35">
        <div className="max-w-5xl mx-auto" data-id="element-244" data-mp="20qrh7bayxt51">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-245" data-mp="25pb69kkuzl1m">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-246" data-mp="25ojg4pa16fiq">
              Rezultatai
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-247" data-mp="1rrw7vqcmo2xi" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-248" data-mp="kdjf7ht70db8">
              DFB Metodologijos Nauda
            </h2>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-249" data-mp="7tvw823fswm1">
            {/* First row: 3 items */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 mb-12" data-id="element-250" data-mp="3q7d899377jmb">
              {[{
              icon: <HeartIcon className="w-12 h-12 text-gold-dark" data-id="element-251" data-mp="3l8sik3lkoic4" />,
              title: 'Nuolatinė Ramybė',
              desc: 'Stabilumas nepriklausomai nuo išorinio pasaulio'
            }, {
              icon: <BrainIcon className="w-12 h-12 text-gold-dark" data-id="element-252" data-mp="3v5oj9n58c064" />,
              title: 'Dėmesio Valdymas',
              desc: 'Sąmoningas dėmesio nukreipimas į svarbius dalykus'
            }, {
              icon: <SparklesIcon className="w-12 h-12 text-gold-dark" data-id="element-253" data-mp="32fye43lks8m5" />,
              title: 'Aktyvi Savireguliacija',
              desc: 'Efektyvus streso ir emocijų valdymas'
            }].map((benefit, i) => <motion.div key={i} variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-254" data-mp="4skla7hin668" data-mpi={i}>
                  {benefit.icon}
                  <div className="w-8 h-px bg-gold mx-auto my-4" data-id="element-255" data-mp="3983y5gbj23bl" />
                  <h3 className="text-xl font-bold text-mountain-darker mb-2" data-id="element-256" data-mp="3d8e26nfrejnt">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-mountain-dark/60 max-w-[180px] mx-auto leading-relaxed" data-id="element-257" data-mp="3i6z6axy4tnhj">
                    {benefit.desc}
                  </p>
                </motion.div>)}
            </div>

            {/* Second row: 2 items centered */}
            <div className="flex flex-col sm:flex-row justify-center gap-12" data-id="element-258" data-mp="12g346b4w5ony">
              {[{
              icon: <Users className="w-12 h-12 text-gold-dark" data-id="element-259" data-mp="2mxc070w3j1rk" />,
              title: 'Emocinis Intelektas',
              desc: 'Geresnis savęs ir kitų supratimas'
            }, {
              icon: <SunIcon className="w-12 h-12 text-gold-dark" data-id="element-260" data-mp="2d796szl2blsp" />,
              title: 'Giluminis Poilsis',
              desc: 'Efektyvus atsigavimas per trumpą laiką'
            }].map((benefit, i) => <motion.div key={i} variants={fadeInUp} className="flex flex-col items-center text-center sm:w-[calc(33.333%-1.5rem)]" data-id="element-261" data-mp="1z92j2c9tv26r" data-mpi={i}>
                  {benefit.icon}
                  <div className="w-8 h-px bg-gold mx-auto my-4" data-id="element-262" data-mp="1pby5v1xymh23" />
                  <h3 className="text-xl font-bold text-mountain-darker mb-2" data-id="element-263" data-mp="3onqwmgu5mq6d">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-mountain-dark/60 max-w-[180px] mx-auto leading-relaxed" data-id="element-264" data-mp="3jq7j5j5a6hfh">
                    {benefit.desc}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── COURSE OFFER ── (dark bg) */}
      <section id="registracija" className="py-24 px-4 bg-mountain-darker text-white" data-id="element-265" data-mp="3hqwbtjhktmw3">
        <div className="max-w-5xl mx-auto" data-id="element-266" data-mp="3moo80pp97v63">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-267" data-mp="38yknc360w9ir">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-268" data-mp="38x9jk3ckv1rn">
              DFB Metodika: Medituoti Lengva
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-269" data-mp="2z1prxt51qp8p" />
            <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-270" data-mp="2q2ma0shm8fvs">
              Prisijunk Prie Kurso
            </h2>
          </motion.div>

          <motion.div className="flex flex-col sm:flex-row items-stretch justify-center mb-12" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-271" data-mp="2bekroplfrj15">
            {[{
            value: '90',
            label: 'Akademinių valandų',
            sub: '30 užsiėmimų po 2–2.5 val.'
          }, {
            value: '2.5',
            label: 'Mėnesiai',
            sub: 'Programos trukmė'
          }, {
            value: '10',
            label: 'Dalyvių max',
            sub: 'Optimali grupė'
          }, {
            value: '110€',
            label: 'už mėnesį',
            sub: 'Galimybė mokėti dalimis'
          }].map((stat, i, arr) => <motion.div key={stat.value} variants={fadeInUp} className="flex-1 flex flex-col items-center text-center px-8 py-8 relative" data-id="element-272" data-mp="25qnuh929sd3l" data-mpi={i}>
                {i < arr.length - 1 && <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2" style={{
              width: 1,
              height: '60%',
              background: 'rgba(255,255,255,0.12)'
            }} data-id="element-273" data-mp="2eqanp7kuiclk" />}
                <span className="font-bold text-gold-light leading-none mb-3" style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)'
            }} data-id="element-274" data-mp="2tht9xn4at3dd">
                  {stat.value}
                </span>
                <p className="text-sm font-semibold text-white uppercase tracking-wide mb-1" data-id="element-275" data-mp="1353vmtcrmbem">
                  {stat.label}
                </p>
                <p className="text-xs text-white/50 leading-relaxed" data-id="element-276" data-mp="10iuqr6lgn7ek">
                  {stat.sub}
                </p>
              </motion.div>)}
          </motion.div>

          <div className="w-full h-px mb-12" style={{
          background: 'rgba(255,255,255,0.1)'
        }} data-id="element-277" data-mp="vql2duzzufee" />

          <motion.div className="text-center" {...fadeInUp} data-id="element-278" data-mp="lvjcuy1t3bba">
            <motion.a href="https://www.instagram.com/vestirna/" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.05,
            boxShadow: '0 20px 50px -10px rgba(0,0,0,0.5)'
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-2.5 whitespace-nowrap text-white px-10 py-[15px] rounded-full text-base font-semibold shadow-2xl transition-colors mb-5" style={{
            background: 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)'
          }} data-id="element-279" data-mp="1jhqlqk557ptg">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-280" data-mp="1t9wsvoqk4015">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-281" data-mp="1y86ryafejks1" />
              </svg>
              Parašyk mums žinutę
            </motion.a>
            <div className="flex items-center justify-center gap-2 mt-3" data-id="element-282" data-mp="35xsx1x14zlko">
              <p className="text-sm text-white/50" data-id="element-283" data-mp="3jqinqerm1eo4">
                Įdomu? Turi klausimų? Tiesiog parašyk ✨
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── COURSE STRUCTURE ── (light bg) */}
      <section id="programa" className="py-24 px-4 bg-sand-light" data-id="element-284" data-mp="2r1lfz5l0z09m">
        <div className="max-w-6xl mx-auto" data-id="element-285" data-mp="2n0rrob3o7jg7">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-286" data-mp="2n1t5uofzgm25">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-287" data-mp="310alrzzncu9i">
              3 etapai: išmok, patirk, pritaikyk
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-288" data-mp="2w2r8b2arwlim" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-289" data-mp="23ibga3d4grig">
              Kurso Struktūra
            </h2>
          </motion.div>

          <motion.div className="space-y-4" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-290" data-mp="2d9fuv1yvyacr">
            {[{
            num: '01',
            title: 'Pirmoji Dalis: Pagrindai',
            desc: 'Baziniai pratimai ir teoriniai pagrindai',
            img: 'https://images.unsplash.com/photo-1712342109846-a8fcb1c883ba?w=1200&q=80'
          }, {
            num: '02',
            title: 'Antroji Dalis: Būsenos',
            desc: '16 aktyvumo būsenų atpažinimas ir valdymas',
            img: 'https://images.unsplash.com/photo-1703945530393-600961291ced?w=1200&q=80'
          }, {
            num: '03',
            title: 'Trečioji Dalis: Integracija',
            desc: 'Įgūdžių taikymas kasdieniame gyvenime',
            img: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=1200&q=80'
          }].map(part => <motion.div key={part.num} variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 200
          }} data-id="element-291" data-mp="33khpu4k4srzi">
                {/* Background image */}
                <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: `url('${part.img}')`
            }} data-id="element-292" data-mp="3hippwlcgs9ew" />
                {/* Left-to-right dark gradient */}
                <div className="absolute inset-0" style={{
              background: 'linear-gradient(to right, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.1) 100%)'
            }} data-id="element-293" data-mp="it7h844xtra0" />
                {/* Ghost number — far right */}
                <div className="absolute right-8 top-1/2 -translate-y-1/2 font-bold select-none leading-none text-white" style={{
              fontSize: '8rem',
              opacity: 0.1
            }} aria-hidden="true" data-id="element-294" data-mp="jnggfcxoakj6">
                  {part.num}
                </div>
                {/* Content — left side */}
                <div className="relative z-10 flex items-center gap-6 px-10 py-10" data-id="element-295" data-mp="1bqzhial7id">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/60 flex items-center justify-center" data-id="element-296" data-mp="3s5mbrw2gtq92">
                    <span className="text-gold-light font-bold text-sm tracking-widest" data-id="element-297" data-mp="3n4qcxwwz0uai">
                      {part.num}
                    </span>
                  </div>
                  <div data-id="element-298" data-mp="1xnky41setl7a">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-1 leading-tight" data-id="element-299" data-mp="1xo4jjsh9qrq2">
                      {part.title}
                    </h3>
                    <p className="text-white/70 text-base md:text-lg leading-relaxed" data-id="element-300" data-mp="27maj43flrlrr">
                      {part.desc}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── (light bg) */}
      <section className="py-24 px-4 bg-sand-lightest overflow-hidden" data-id="element-301" data-mp="1jxhi3sk4inqa">
        <div className="max-w-6xl mx-auto" data-id="element-302" data-mp="1nyb4ghhx3uh2">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-303" data-mp="1nwgkb5eh06e1">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-304" data-mp="1sq2zb483wmd0">
              Tikros transformacijos istorijos
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-305" data-mp="w5aov29p2wv3" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-306" data-mp="c6lkdwab26ye">
              Dalyvių Atsiliepimai
            </h2>
          </motion.div>

          {(() => {
          const testimonials = [{
            name: 'Eglė V.',
            role: 'Kursų dalyvė',
            quote: 'Nuo pirmos dienos jaučiau, kad patekau į saugią ir gerai apgalvotą mokymosi struktūrą, bet kartu apsupta labai daug šilumos ir palaikymo. DFB padėjo man atpažinti ir naviguoti neramius jausmus ir chaotiškas būsenas, bei juos keisti. Sugrįžti į čia ir dabar. Prisiimti atsakomybę už savo dėmesį. Nuosekliai kartojant pratimus ir darant praktikas įvyko pozityvus pokytis.'
          }, {
            name: 'Ieva M.',
            role: 'Kursų dalyvė',
            quote: 'Labiausiai patiko, kad 99% mokymų buvo patyrimas. Suvokimas visko per save. Būtent todėl DFB lengvai integravosi į mano gyvenimą. Perskaičiau ne vieną aprašymą apie DFB internete - visiškai neatspindi to, ką gauni. O gauni be galo daug. Sakyčiau, kad DFB - didelis asmeninis virsmas.'
          }, {
            name: 'Mantas R.',
            role: 'Kursų dalyvis',
            quote: 'Pagrindiniai įsisamoninimai ir suvokimai atėję nuoširdžiai praktikuojant ir naudojant DFB metodą yra: išmokimas, kaip keisti savo būsenas ir stebėti kaip per tai keičiasi ir aplinka, žmonės bei jų reakcijos; suvokimas, kaip įgalinti savo naudai visas būsenas neskirstant jų į geras ir blogas.'
          }, {
            name: 'Dovydas S.',
            role: 'Kursų dalyvis',
            quote: 'Labai aiškiai ir struktūruotai perteikiama kurso informacija, palydima per patyrimus. Isiklausymas į tai, kas kiekvienam aktualu, koks kiekvieno mokinio iššūkis, ir atsižvelgimas į tai. Rūpestis erdve, atmosfera, lankstumas situacijoms. Nuolatinis palaikymas, domėjimasis, įkvėpimas per konkrečius pavyzdžius. Dėkui už transformaciją ir nepakartojama pokytį.'
          }];
          const goToTestimonial = (idx: number) => {
            setTestimonialDirection(idx > activeTestimonial ? 1 : -1);
            setActiveTestimonial(idx);
          };
          const prevTestimonial = () => goToTestimonial(activeTestimonial === 0 ? testimonials.length - 1 : activeTestimonial - 1);
          const nextTestimonial = () => goToTestimonial(activeTestimonial === testimonials.length - 1 ? 0 : activeTestimonial + 1);
          const currentTestimonial = testimonials[activeTestimonial];
          const slideVariants = {
            enter: (dir: number) => ({
              opacity: 0,
              x: dir * 60
            }),
            center: {
              opacity: 1,
              x: 0
            },
            exit: (dir: number) => ({
              opacity: 0,
              x: dir * -60
            })
          };
          return <div className="relative flex flex-col items-center" data-id="element-307" data-mp="e5es9ycrlnbz">
                <div className="flex items-center w-full gap-4 md:gap-8" data-id="element-308" data-mp="j3q249327xu7">
                  {/* Left arrow */}
                  <button onClick={prevTestimonial} aria-label="Ankstesnis" className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/40 bg-white hover:bg-gold hover:border-gold text-gold-dark hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm" data-id="element-309" data-mp="n37c9ftflj5p">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-310" data-mp="16rzmr7ojefjr">
                      <polyline points="15 18 9 12 15 6" data-id="element-311" data-mp="211ryaj5brc7r" />
                    </svg>
                  </button>

                  {/* Testimonial content */}
                  <div className="flex-1 min-w-0 overflow-hidden" data-id="element-312" data-mp="18gi778j35pqk">
                    <AnimatePresence mode="wait" custom={testimonialDirection} data-id="element-313" data-mp="14e52mng2rxvd">
                      <motion.div key={activeTestimonial} custom={testimonialDirection} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                    duration: 0.45,
                    ease: 'easeInOut'
                  }} className="flex flex-col items-center text-center px-2 md:px-8" data-id="element-314" data-mp="1i59xlc8i01zd">
                        {/* Decorative quote mark */}
                        <span className="font-serif text-gold/25 select-none leading-none mb-2" style={{
                      fontSize: '6rem',
                      lineHeight: 0.8
                    }} aria-hidden="true" data-id="element-315" data-mp="2nd323gx4tbpg">
                          &ldquo;
                        </span>

                        {/* Quote text — dynamically from current testimonial */}
                        <p className="text-mountain-darker text-lg md:text-xl leading-relaxed whitespace-pre-line max-w-2xl mx-auto mb-10 font-[400]" data-id="element-316" data-mp="3b1vtr16q0xe9">
                          {currentTestimonial.quote}
                        </p>

                        {/* Gold divider */}
                        <div className="w-12 h-px bg-gold mb-4" data-id="element-317" data-mp="1zkmb2gvf29e0" />

                        {/* Name & role */}
                        <p className="font-bold text-mountain-darker text-lg" data-id="element-318" data-mp="2d2nnpiwnl40m">
                          {currentTestimonial.name}
                        </p>
                        <p className="text-sm text-mountain-dark/60 mt-0.5" data-id="element-319" data-mp="2i2s65eh4g8th">
                          {currentTestimonial.role}
                        </p>
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Right arrow */}
                  <button onClick={nextTestimonial} aria-label="Kitas" className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/40 bg-white hover:bg-gold hover:border-gold text-gold-dark hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm" data-id="element-320" data-mp="1ucxu2z4mmoaa">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-321" data-mp="1bl51h5tgz533">
                      <polyline points="9 18 15 12 9 6" data-id="element-322" data-mp="3fxcouczytln8" />
                    </svg>
                  </button>
                </div>

                {/* Dot indicators */}
                <div className="flex items-center gap-3 mt-10" data-id="element-323" data-mp="35ywum15n5w9i">
                  {testimonials.map((_, i) => <button key={i} onClick={() => goToTestimonial(i)} aria-label={`Atsiliepimas ${i + 1}`} className="transition-all duration-300 rounded-full" style={{
                width: i === activeTestimonial ? '2rem' : '0.5rem',
                height: '0.5rem',
                background: i === activeTestimonial ? 'rgb(var(--gold))' : 'rgba(var(--gold), 0.3)'
              }} data-id="element-324" data-mp="314ivporrt703" data-mpi={i} />)}
                </div>
              </div>;
        })()}
        </div>
      </section>

      {/* ── TEAM ── (dark bg) */}
      <section id="komanda" className="py-32 px-4 bg-mountain-darker text-white" data-id="element-325" data-mp="23lcapn99jq5w">
        <div className="max-w-4xl mx-auto" data-id="element-326" data-mp="2rbexbnues3x4">
          <motion.div className="text-center mb-20" {...fadeInUp} data-id="element-327" data-mp="2wa9gws079wia">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-328" data-mp="2wbkkortnb49e">
              Patyrę instruktoriai ir praktikai
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-329" data-mp="2mj533vm9ghp1" />
            <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-330" data-mp="13l8t4bmyvsw7">
              Mūsų Komanda
            </h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-331" data-mp="ymxfrqxgr098">
            <motion.div variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-332" data-mp="kn5x3cw04vow">
              <div className="relative w-52 h-52 mx-auto mb-8 rounded-full overflow-hidden ring-2 ring-gold ring-offset-8 ring-offset-mountain-darker flex-shrink-0" data-id="element-333" data-mp="fpnyafbp7wum">
                <div className="absolute inset-0 bg-cover" style={{
                backgroundImage: "url('https://cdn.magicpatterns.com/uploads/p5KUhhvxDrMn2D53eKDHKt/agne3.jpg')",
                backgroundPosition: 'center 30%'
              }} data-id="element-334" data-mp="pko613a0raww" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-335" data-mp="fic3qrgyfncw">
                Agnė Kubertė
              </h3>
              <p className="text-sm uppercase tracking-widest text-gold-light font-semibold mb-6" data-id="element-336" data-mp="jjxqd4jajg0a">
                DFB metodologijos Instruktorė
              </p>
              <div className="w-12 h-px bg-gold/40 mb-6" data-id="element-337" data-mp="ogxmonqc94e7" />
              <p className="text-white/70 leading-relaxed mb-8 max-w-xs" data-id="element-338" data-mp="thlng14dczlm">
                DFB metodikos sertifikuota instruktorė, daugiau nei 10 metų
                meditacijų praktikė. Mokymuose ji padeda žmonėms sujungti
                meditaciją su kasdieniu gyvenimu – įnešti daugiau aiškumo,
                sąmoningo buvimo ir vidinės ramybės. Padeda dalyviams išmokti
                savireguliacijos ir lengvai pritaikyti meditacijos principus
                realiose gyvenimo situacijose.
              </p>
              <div className="relative max-w-xs" data-id="element-339" data-mp="1h88uyladhn7n">
                <span className="absolute -top-4 -left-2 font-serif text-gold/30 select-none leading-none" style={{
                fontSize: '4rem',
                lineHeight: 1
              }} aria-hidden="true" data-id="element-340" data-mp="1rcfl7s6pyz6a">
                  "
                </span>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-341" data-mp="2a24ab4nw3ldq">
              <div className="relative w-52 h-52 mx-auto mb-8 rounded-full overflow-hidden ring-2 ring-gold ring-offset-8 ring-offset-mountain-darker flex-shrink-0" data-id="element-342" data-mp="254lxh36r4m0g">
                <div className="absolute inset-0 bg-cover" style={{
                backgroundImage: "url('https://cdn.magicpatterns.com/uploads/cYk35xR7d8THHiA5ofpAZk/Screenshot_2026-03-06_at_13.56.34.png')",
                backgroundPosition: 'center 40%'
              }} data-id="element-343" data-mp="1wbiphaevl1d7" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-344" data-mp="r3ptxv87z1z9">
                Vestina Zablockytė
              </h3>
              <p className="text-sm uppercase tracking-widest text-gold-light font-semibold mb-6" data-id="element-345" data-mp="cfcvmrlnt71l">
                DFB metodologijos Instruktorė
              </p>
              <div className="w-12 h-px bg-gold/40 mb-6" data-id="element-346" data-mp="hbjtry7tde8z" />
              <p className="text-white/70 leading-relaxed mb-8 max-w-xs" data-id="element-347" data-mp="3dc2o6xgl75q">
                „Sąmoningas judėjimas, šokis, rebefingas, Vipasana... Per 15
                metų leidausi į daugybę savęs pažinimo kelionių ir išbandžiau
                pačias įvairiausias praktikas. Tačiau būtent DFB metodika, kuria
                aktyviai gyvenu pastaruosius šešerius metus, tapo ta praktika,
                kuri iš esmės pakeitė mano kasdienybės kokybę. Šiandien noriu
                pasidalinti atradimu, kad medituoti lengva! Vietoj ilgų paieškų
                kviečiu iškart nerti į procesą, kuris atveria neįtikėtinai platų
                galimybių spektrą. Prisijunk!“
              </p>
              <div className="relative max-w-xs" data-id="element-348" data-mp="38d2c5p39zqyx">
                <span className="absolute -top-4 -left-2 font-serif text-gold/30 select-none leading-none" style={{
                fontSize: '4rem',
                lineHeight: 1
              }} aria-hidden="true" data-id="element-349" data-mp="38a6gy8itbge6">
                  "
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SCHEDULE ── (light bg) */}
      <section id="tvarkarastis" className="pt-24 pb-12 px-4 bg-sand-lightest" data-id="element-350" data-mp="9tlx4uaqdxn6">
        <div className="max-w-6xl mx-auto" data-id="element-351" data-mp="4vtx1czxn57w">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-352" data-mp="2blqq893yv0zh">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-353" data-mp="2bm0e9r5gay2j">
              Prisijunk prie rudens grupės
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-354" data-mp="1ktrlbm7pqhqd" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-355" data-mp="1uk2ze1l427az">
              Artėjantis Kursas
            </h2>
          </motion.div>

          <CourseCalendar course={currentCourse} data-id="element-356" data-mp="11vfazx52f1t7" />

          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12" {...fadeInUp} data-id="element-357" data-mp="11wqfjz6c4dld">
            <motion.button onClick={onViewCourses} whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.97
          }} transition={{
            duration: 0.15,
            ease: [0.23, 1, 0.32, 1]
          }} className="inline-flex items-center gap-2 border border-mountain-darker/25 text-mountain-darker px-9 py-[13px] rounded-full text-base font-semibold bg-white" data-id="element-358" data-mp="1bsjxxz6qj1b2">
              Kiti kursai
              <ArrowRightIcon className="w-4 h-4" data-id="element-359" data-mp="blesn3ghheqa" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ── KONTAKTAI (4 variantų peržiūra) ── */}
      <ContactSection data-id="element-360" data-mp="1uagscm3u9zo" />

      {/* ── FOOTER ── */}
      <footer className="py-12 px-4 bg-sand-lightest border-t border-sand-dark/20" data-id="element-361" data-mp="3t0v5hpoot5tj">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-362" data-mp="3ozjat5qyhueh">
          <div className="flex items-center gap-2" data-id="element-363" data-mp="3k0eza8gk7kkz">
            <span className="text-gold-dark font-accent text-xl tracking-wide font-[500]" data-id="element-364" data-mp="3k0ykpz5f4r3r">
              tavo būsena
            </span>
          </div>
          <p className="text-mountain-dark/50 text-sm" data-id="element-365" data-mp="1i8tjty5xyjgw">
            © 2026 tavo būsena. Visos teisės saugomos.
          </p>
          <div className="flex items-center gap-6" data-id="element-366" data-mp="1db0grdzxq5ku">
            <a href="#apie" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-367" data-mp="1d85xvqwkkstj">
              Apie
            </a>
            <a href="#metodika" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-368" data-mp="2071obsbsu5mj">
              Metodika
            </a>
            <a href="#registracija" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-369" data-mp="1qd2o4yvacvx8">
              Registracija
            </a>
          </div>
        </div>
      </footer>
    </main>;
}